import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Paperclip, X, FileText, Image as ImageIcon, Video as VideoIcon, ArrowRight, DollarSign, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const FILE_MAX_BYTES = 50 * 1024 * 1024;
const MAX_ATTACHMENTS = 8;

/** /jobs/new — Consumer creates a Request for Quotation (RFQ).
 *  Mirrors Alibaba's "Request a Quote" flow. After submit, every vendor
 *  in the matching category gets emailed + in-app pinged. */
export default function NewJobRequestPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category_id: '',
    category_name: '',
    budget_min: '',
    budget_max: '',
    turnaround_days: '',
    deadline_iso: '',
    location: '',
  });
  const [attachments, setAttachments] = useState([]); // [{url, filename, content_type}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth?next=/jobs/new');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/categories/`);
        if (res.ok) setCategories(await res.json());
      } catch {}
    })();
  }, [user, authLoading, navigate]);

  const onFile = async (fileList) => {
    if (!fileList?.length) return;
    if (attachments.length + fileList.length > MAX_ATTACHMENTS) {
      toast.error(`Max ${MAX_ATTACHMENTS} attachments`);
      return;
    }
    setUploading(true);
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const next = [...attachments];
    for (const f of fileList) {
      if (f.size > FILE_MAX_BYTES) { toast.error(`Skipped: ${f.name} — over 50MB`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(`${API}/media/upload?category=rfq`, { method: 'POST', headers, body: fd });
      // eslint-disable-next-line no-await-in-loop
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || `Upload failed: ${f.name}`); continue; }
      const url = data.url?.startsWith('http') ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      next.push({ url, filename: f.name, content_type: f.type });
    }
    setAttachments(next);
    setUploading(false);
  };

  const removeAttachment = (idx) => setAttachments((cur) => cur.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault();
    if (form.title.trim().length < 4) return toast.error('Title is too short');
    if (form.description.trim().length < 10) return toast.error('Add a bit more detail (min 10 chars)');
    setSubmitting(true);
    const body = {
      ...form,
      budget_min: form.budget_min ? Number(form.budget_min) : null,
      budget_max: form.budget_max ? Number(form.budget_max) : null,
      turnaround_days: form.turnaround_days ? Number(form.turnaround_days) : null,
      deadline_iso: form.deadline_iso || null,
      attachments,
    };
    try {
      const res = await fetch(`${API}/rfq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Could not post your request'); setSubmitting(false); return; }
      const notified = data.request?.vendors_notified || 0;
      toast.success(`Posted! ${notified} ${notified === 1 ? 'vendor' : 'vendors'} notified.`);
      navigate(`/jobs/${data.request.request_id}`);
    } catch {
      toast.error('Network error');
      setSubmitting(false);
    }
  };

  const fileIcon = (ct) => (ct?.startsWith('image/') ? ImageIcon : ct?.startsWith('video/') ? VideoIcon : FileText);

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-12" data-testid="rfq-new-page">
        <div className="mb-8">
          <p className="inline-flex items-center gap-2 text-xs text-fuchsia-300 font-bold tracking-widest uppercase mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
            Request a Job
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold font-syne leading-tight">
            Tell us what you need.<br />
            <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent">
              Indigenous vendors come to you.
            </span>
          </h1>
          <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
            No browsing. Post your request — every vendor in the matching category gets notified
            and submits a quote. You pick the best offer and start a conversation.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {/* Title */}
          <Field label="What do you need?" required>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. I need a flyer with 2-day turnaround"
              maxLength={140}
              className="form-input"
              data-testid="rfq-title"
            />
          </Field>

          {/* Description */}
          <Field label="Describe the project" required hint="Be specific — colors, dimensions, deliverables, brand vibe.">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={5}
              maxLength={5000}
              placeholder="Theme: Indigenous bakery. 1-page promotional flyer. Need PSD source + PDF for print. Yellow + black palette. Logo included…"
              className="form-input resize-none"
              data-testid="rfq-description"
            />
          </Field>

          {/* Category */}
          <Field label="Category">
            <select
              value={form.category_id}
              onChange={(e) => {
                const cid = e.target.value;
                const found = categories.find((c) => c.category_id === cid);
                setForm({ ...form, category_id: cid, category_name: found?.name || '' });
              }}
              className="form-input"
              data-testid="rfq-category"
            >
              <option value="">Any category</option>
              {categories.map((c) => (
                <option key={c.category_id} value={c.category_id}>{c.name}</option>
              ))}
            </select>
          </Field>

          {/* Budget + Deadline + Turnaround in a 3-col grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Budget min" icon={DollarSign}>
              <input type="number" min="0" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })} placeholder="50" className="form-input" data-testid="rfq-budget-min" />
            </Field>
            <Field label="Budget max" icon={DollarSign}>
              <input type="number" min="0" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} placeholder="150" className="form-input" data-testid="rfq-budget-max" />
            </Field>
            <Field label="Turnaround (days)" icon={Clock}>
              <input type="number" min="1" value={form.turnaround_days} onChange={(e) => setForm({ ...form, turnaround_days: e.target.value })} placeholder="2" className="form-input" data-testid="rfq-turnaround" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Need it by (optional)">
              <input type="date" value={form.deadline_iso?.slice(0, 10) || ''} onChange={(e) => setForm({ ...form, deadline_iso: e.target.value })} className="form-input" data-testid="rfq-deadline" />
            </Field>
            <Field label="Location (optional)" icon={MapPin}>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Remote / city, state" className="form-input" data-testid="rfq-location" />
            </Field>
          </div>

          {/* Attachments */}
          <Field label="Examples / reference files (optional)" hint={`Up to ${MAX_ATTACHMENTS} files · 50MB each.`}>
            <div className="space-y-2">
              {attachments.length > 0 && (
                <div className="space-y-1.5" data-testid="rfq-attachments-list">
                  {attachments.map((a, i) => {
                    const Icon = fileIcon(a.content_type);
                    return (
                      <div key={a.url} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                        <Icon size={16} className="text-fuchsia-300 flex-shrink-0" />
                        <span className="text-sm text-zinc-200 flex-1 truncate">{a.filename || `Attachment ${i + 1}`}</span>
                        <button type="button" onClick={() => removeAttachment(i)} className="text-zinc-400 hover:text-red-400" data-testid={`rfq-attach-remove-${i}`}>
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-fuchsia-500/30 hover:border-fuchsia-400 bg-white/5 text-fuchsia-200 text-sm disabled:opacity-50"
                data-testid="rfq-attach-pick"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                {uploading ? 'Uploading…' : 'Add files'}
              </button>
              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf,.heic,.heif" className="hidden" onChange={(e) => { onFile(Array.from(e.target.files || [])); e.target.value = ''; }} />
            </div>
          </Field>

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-6 py-3 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold flex items-center justify-center gap-2 shadow-[0_8px_24px_-8px_rgba(250,204,21,0.6)] disabled:opacity-60"
              data-testid="rfq-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Posting…' : 'Post Request & Notify Vendors'}
              <ArrowRight size={14} />
            </button>
            <p className="text-xs text-zinc-500 mt-3">
              Your request is sent to vendors in the matching category. You'll get a notification each time someone offers.
            </p>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, hint, icon: Icon, required, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-300 flex items-center gap-1 mb-1.5">
      {Icon && <Icon size={11} className="text-fuchsia-300" />}
      {label}
      {required && <span className="text-fuchsia-400">*</span>}
    </span>
    {children}
    {hint && <span className="text-[11px] text-zinc-500 mt-1 block">{hint}</span>}
  </label>
);
